import type { AsyncData } from 'nuxt/app'
import type { NetworkClient } from '~/lib/bosca/networkclient'
import { Api } from '~/lib/bosca/api'
import {
    type BibleChapterContentFragment,
    type CollectionFragment,
    type DocumentFragment,
    type MetadataFragment,
    GetBibleMetadataChapterDocument,
    GetCollectionCollectionItemsDocument,
    GetCollectionExpandedItemsDocument,
    GetContentBySlugDocument,
    GetDailyDayDocument,
    GetDailyStepDocument,
    GetDocumentDocument,
    GetItemsDocument,
    GetStudyDayDocument,
    SearchDocument,
} from '~/lib/graphql/graphql'
import type { Document } from '~/lib/bosca/document'

export type Items = {
    collection: CollectionFragment
    items: Array<MetadataFragment | CollectionFragment>
    count: number
}

export type SearchResults = {
    items: Array<MetadataFragment | CollectionFragment>
    facets: { field: string, value: string, count: number }[]
    count: number
}

export class Content<T extends NetworkClient> extends Api<T> {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(network: T) {
        super(network)
    }

    async getDaily(date: Date): Promise<MetadataFragment> {
        const d = new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours() - 6,
        )
        const dateStr = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate() + 'T00:00:00.000Z'
        const response = await this.network.execute(GetDailyDayDocument, {
            slug: 'daily-' + d.getFullYear(),
            date: dateStr,
        })
        return response?.content?.slug as MetadataFragment
    }

    async getDailyStep(slug: string, id: number): Promise<MetadataFragment> {
        const response = await this.network.execute(GetDailyStepDocument, {
            slug,
            id,
        })
        return response?.content?.slug as MetadataFragment
    }

    getExpandedItemsAsync(
        slug: string,
        offset: number | Ref<number, null>,
        limit: number | Ref<number, null>,
    ): AsyncData<Items | null, any> {
        const route = useRoute()
        return this.executeAndTransformAsyncData(
            GetCollectionExpandedItemsDocument,
            {
                slug,
                offset,
                limit,
                state: route.query.preview === 'true' ? undefined : 'published',
            },
            (response) => {
                const items = response?.content?.slug
                if (!items) {
                    return {
                        collection: null,
                        items: [],
                        count: 0,
                    } as unknown as Items
                }
                return {
                    collection: items as CollectionFragment,
                    // @ts-expect-error: this is correct
                    items: items.expandedMetadata,
                    // @ts-expect-error: this is correct
                    count: items.expandedMetadataCount,
                }
            },
        )
    }

    async getItems(
        slug: string,
        offset: number | Ref<number, null>,
        limit: number | Ref<number, null>,
    ): Promise<Items> {
        const route = useRoute()
        const response = await this.network.execute(GetItemsDocument, {
            slug,
            offset,
            limit,
            state: route.query.preview === 'true' ? undefined : 'published',
        })
        const items = response?.content?.slug
        if (!items) {
            return {
                collection: { __typename: 'Collection' },
                items: [],
                count: 0,
            } as unknown as Items
        }
        if (items?.__typename !== 'Collection') {
            throw new Error('invalid type: ' + items?.__typename)
        }
        return {
            collection: items as CollectionFragment,
            items: items.items,
            count: items.itemsCount,
        }
    }

    getItemsAsyncData(
        slug: string,
        offset: number | Ref<number, null>,
        limit: number | Ref<number, null>,
    ): AsyncData<Items | null, any> {
        const route = useRoute()
        return this.executeAndTransformAsyncData(
            GetItemsDocument,
            {
                slug,
                offset,
                limit,
                state: route.query.preview === 'true' ? undefined : 'published',
            },
            (response) => {
                const items = response?.content?.slug
                if (!items) {
                    return {
                        collection: { __typename: 'Collection' },
                        items: [],
                        count: 0,
                    } as unknown as Items
                }
                if (items.__typename !== 'Collection') {
                    console.error('invalid type: ' + items.__typename)
                    throw new Error('invalid type: ' + items.__typename)
                }
                return {
                    collection: items as CollectionFragment,
                    items: items.items,
                    count: items.itemsCount,
                } as Items
            },
        )
    }

    async getDocument(
        id: string,
    ): Promise<Document> {
        const response = await this.network.execute(GetDocumentDocument, { id })
        const document = response?.content?.metadata?.document as DocumentFragment
        return document.content.document as Document
    }

    async getContent(
        slug: string,
    ): Promise<MetadataFragment | CollectionFragment> {
        const response = await this.network.execute(GetContentBySlugDocument, {
            slug,
        })
        return response?.content?.slug as
            | MetadataFragment
            | CollectionFragment
    }

    getCollectionsAsyncData(
        slug: string,
        offset: number | Ref<number, null>,
        limit: number | Ref<number, null>,
    ): AsyncData<
        {
            collection: CollectionFragment
            collections: CollectionFragment[]
            count: number
        } | null, any
    > {
        return this.executeAndTransformAsyncData(
            GetCollectionCollectionItemsDocument,
            {
                slug,
                offset,
                limit,
            },
            (response) => {
                return {
                    collection: response?.content?.slug as CollectionFragment,
                    collections: response?.content?.slug
                        // @ts-expect-error: this is correct
                        ?.collections as CollectionFragment[],
                    // @ts-expect-error: this is correct
                    count: response?.content?.slug?.collectionsCount,
                } as { collection: CollectionFragment, collections: CollectionFragment[], count: number }
            },
        )
    }

    async getStudyDay(
        slug: string,
        dayOffset: number,
    ): Promise<
        { metadata: MetadataFragment, step: MetadataFragment, steps: number[], days: number }
    > {
        const response = await this.network.execute(GetStudyDayDocument, {
            slug,
            dayOffset,
        })
        if (response?.content?.slug?.__typename !== 'Metadata') {
            throw new Error('invalid type')
        }
        return {
            metadata: response?.content?.slug as MetadataFragment,
            step: response?.content?.slug?.guide?.stepByOffset?.metadata as MetadataFragment,
            steps: response?.content?.slug?.guide?.steps?.map(s => s.id) || [],
            days: response?.content?.slug?.guide?.stepCount || 0,
        }
    }

    searchAsyncData(
        query: string | Ref<string, string>,
        filter: string | Ref<string | null, string | null> | null,
        sort: string | Ref<string | null, string | null> | null,
        offset: number | Ref<number, null>,
        limit: number | Ref<number, null>,
    ): AsyncData<SearchResults | null, any> {
        return this.executeAndTransformAsyncData(
            SearchDocument,
            {
                query,
                filter,
                sort,
                offset,
                limit,
            },
            (response) => {
                // @ts-expect-error: this is correct
                const items = response?.search?.documents
                if (!items) {
                    return {
                        items: [],
                        facets: [],
                        count: 0,
                    } as unknown as SearchResults
                }
                return {
                    items: items.map((i: any) => i.metadata || i.collection || i.profile),
                    // @ts-expect-error: this is correct
                    facets: response?.search?.facets || [],
                    // @ts-expect-error: this is correct
                    count: response?.search?.estimatedHits || 0,
                } as SearchResults
            },
        )
    }

    async getBibleChapter(
        id: string,
        usfm: string,
    ): Promise<BibleChapterContentFragment> {
        const response = await this.network.execute(
            GetBibleMetadataChapterDocument,
            {
                id,
                usfm,
            },
        )
        return response!.content!.metadata!.bible!
            .chapter as BibleChapterContentFragment
    }
}
